/* eslint-disable @typescript-eslint/no-unsafe-return */
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardMedia,
  IconButton,
  Typography,
} from '@material-ui/core';
import { Theme, makeStyles } from '@material-ui/core/styles';
import React, { useContext, useRef, useState } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import Slider, { Settings } from 'react-slick';
import { AppsI } from '../..';
import colorContext from '../../../../Context/colorContext';

interface Props {
  listApps: AppsI;
}

export default function Apps({ listApps }: Props): JSX.Element {
  const { colors } = useContext(colorContext);
  const useStyles = makeStyles((theme: Theme) => ({
    main: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      backgroundColor: colors.colorBackground,
      alignItems: 'center',
      borderBottom: `1px solid ${colors.dividerColor}`,
      padding: '0px 16px',
    },
    card: {
      position: 'relative',
      width: 248,
      height: 248,
      borderRadius: 4,
      boxShadow: 'none',
      overflow: 'visible',
      textTransform: 'capitalize',
      color: colors.accentColor,
      fontSize: theme.typography.pxToRem(18),
      backgroundColor: colors.colorBackground,
      '&:hover': {
        boxShadow: '0px 12px 42px -4px rgba(24, 39, 75, 0.12)',
        backgroundColor: colors.colorBackground,
        '& $cardOverlay': {
          display: 'flex',
        },
      },
      [theme.breakpoints.down('sm')]: {
        height: 200,
        margin: '10px auto',
      },
      boxSizing: 'border-box',
    },
    cardOverlay: {
      display: 'none',
      position: 'absolute',
      height: '100%',
      width: '100%',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'space-around',
      zIndex: 1,
      background: colors.cardAccentColor,
    },
    textTitle: {
      marginBottom: 16,
      fontWeight: 800,
      color: colors.textBody,
      fontSize: theme.typography.pxToRem(32),
      lineHeight: '39.4px',
      [theme.breakpoints.down('sm')]: {
        fontSize: theme.typography.pxToRem(24),
        textAlign: 'center',
      },
    },
    textSubTitle: {
      fontSize: theme.typography.pxToRem(18),
      color: colors.subTitleColor,
      fontWeight: 400,
      lineHeight: '28.8px',
      [theme.breakpoints.down('sm')]: {
        fontSize: theme.typography.pxToRem(16),
        textAlign: 'center',
      },
    },
    cardActionArea: {
      height: '100%',
      width: '248px',
      [theme.breakpoints.down('sm')]: {
        height: '100%',
        width: '100%',
      },
    },
    appName: {
      color: colors.colorBackground,
      textTransform: 'none',
      textAlign: 'center',
      fontSize: theme.typography.pxToRem(20),
      fontWeight: 600,
      lineHeight: '27.24px',
    },
    appButton: {
      border: `1px solid ${colors.colorBackground}`,
      width: '100%',
      padding: '10px 52px',
      color: colors.colorBackground,
      fontSize: theme.typography.pxToRem(14),
      fontWeight: 600,
      textTransform: 'uppercase',
      borderRadius: 5,
      backgroundColor: colors.accentColor,

      '&:first-child': {
        marginBottom: 10,
      },

      '&:hover': {
        backgroundColor: colors.colorBackground,
        transition: 'all 0.2s ease',
        color: colors.accentColor,
      },
    },
    buttonNavigate: {
      color: colors.accentColor,
      width: 40,
      height: 40,
      border: `1px solid ${colors.accentColor}`,
      '&:first-child': {
        borderRadius: '4px 0px 0px 4px',
      },
      '&:last-child': {
        borderRadius: '0px 4px 4px 0px',
      },
      '&:hover': {
        backgroundColor: colors.cardAccentColor,
        color: colors.textAccentColor,
      },
    },
    buttonLabel: {
      width: 'auto',
    },
    iconNavigate: {
      width: 24,
      height: 24,
      fontSize: 24,
    },
    slider: {
      '& .slick-list': {
        margin: 0,
        padding: '0 16px',
        height: 248,
        [theme.breakpoints.down('sm')]: {
          padding: 0,
        },
        '& .slick-track': {
          height: 248,
        },
      },
      '& .slick-slide': {
        padding: '0 8px',
        [theme.breakpoints.down('sm')]: {
          padding: 0,
        },
        boxSizing: 'border-box',
      },
      [theme.breakpoints.down('sm')]: {
        '& .slick-slide': {
          padding: '0 4px',
        },
      },
    },
    info: {
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      marginBottom: '48px',
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'center',
        textAlign: 'center',
        flexDirection: 'column',
      },
    },
    controls: {
      display: 'flex',
      alignItems: 'center',
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
  }));
  const classes = useStyles();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [appSelected, setAppSelected] = useState<number | null>(null);
  const sliderRef = useRef<Slider | null>(null);

  const NextArrow = (): JSX.Element => (
    <IconButton
      classes={{
        root: classes.buttonNavigate,
        label: classes.buttonLabel,
      }}
      color='primary'
      onClick={() => sliderRef.current?.slickNext()}
      aria-label='botão avançar'
    >
      <MdChevronRight className={classes.iconNavigate} />
    </IconButton>
  );
  const PrevArrow = (): JSX.Element => (
    <IconButton
      classes={{
        root: classes.buttonNavigate,
        label: classes.buttonLabel,
      }}
      color='primary'
      onClick={() => sliderRef.current?.slickPrev()}
      aria-label='botão retroceder'
    >
      <MdChevronLeft className={classes.iconNavigate} />
    </IconButton>
  );

  const settings: Settings = {
    speed: 1000,
    arrows: false,
    slidesToShow: listApps.results.length < 4 ? listApps.results.length : 4,
    slidesToScroll: 1,
    autoplay: true,
    infinite: listApps.results.length > 4,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow:
            listApps.results.length < 3 ? listApps.results.length : 3,
          slidesToScroll: 1,
          infinite: listApps.results.length > 3,
        },
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow:
            listApps.results.length < 2 ? listApps.results.length : 2,
          slidesToScroll: 1,
          infinite: listApps.results.length > 2,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: listApps.results.length > 1,
        },
      },
    ],
  };

  return (
    <Box className={classes.main}>
      <Box
        display='flex'
        maxWidth='1040px'
        width='100%'
        flexDirection='column'
        padding='64px 0px'
      >
        <Box className={classes.info}>
          <Box width='100%'>
            <Typography className={classes.textTitle} variant='h1'>
              Apps do governo
            </Typography>
            <Typography className={classes.textSubTitle}>
              Conheça os aplicativos recomendados.
            </Typography>
          </Box>
          <Box className={classes.controls}>
            <PrevArrow />
            <NextArrow />
          </Box>
        </Box>
        <Box>
          <Slider {...settings} ref={sliderRef} className={classes.slider}>
            {listApps.results.map(app => (
              <Card
                onMouseEnter={(): void => setAppSelected(app.id)}
                onMouseLeave={(): void => setAppSelected(null)}
                key={app.id}
                className={classes.card}
              >
                <Box className={classes.cardOverlay}>
                  <Typography className={classes.appName}>
                    {app.nome}
                  </Typography>
                  <Box display='flex' flexDirection='column'>
                    <Button
                      onClick={(): void => (window as any).open(app.url_apple)}
                      className={classes.appButton}
                      style={{ display: app.url_apple ? 'block' : 'none' }}
                      variant='outlined'
                    >
                      App Store
                    </Button>
                    <Button
                      onClick={(): void => (window as any).open(app.url_google)}
                      className={classes.appButton}
                      style={{ display: app.url_google ? 'block' : 'none' }}
                      variant='outlined'
                    >
                      Google Play
                    </Button>
                  </Box>
                </Box>
                <CardActionArea className={classes.cardActionArea}>
                  <CardMedia
                    style={{
                      height: '100%',
                    }}
                    image={app.url_logo}
                  />
                </CardActionArea>
              </Card>
            ))}
          </Slider>
        </Box>
      </Box>
    </Box>
  );
}
